/**
 * RequirePermission component
 * Conditionally renders children based on user permissions
 * Used to hide UI elements from users who lack specific permissions
 */
import { usePermissions } from '@/hooks/usePermissions'

export interface RequirePermissionProps {
  /** The permission to check (e.g., 'config:edit', 'session:delete') */
  permission: string
  /** Content to render if user has permission */
  children: React.ReactNode
  /** Optional content to render if user lacks permission */
  fallback?: React.ReactNode
}

/**
 * Renders children only if the current user has the specified permission
 * 
 * @example
 * ```tsx
 * // Hide delete button from users without permission
 * <RequirePermission permission="config:edit">
 *   <Button onClick={handleDelete}>Delete</Button>
 * </RequirePermission>
 * 
 * // Show alternative content for unauthorized users
 * <RequirePermission 
 *   permission="project:create" 
 *   fallback={<span>Contact admin</span>}
 * >
 *   <Button onClick={handleCreate}>Create Project</Button>
 * </RequirePermission>
 * ```
 */
export function RequirePermission({ 
  permission, 
  children, 
  fallback = null 
}: RequirePermissionProps) {
  const { hasPermission, isLoading } = usePermissions()

  // Don't render anything while loading to prevent flash
  if (isLoading) {
    return null
  }

  // Render children if user has permission, otherwise fallback
  if (!hasPermission(permission)) {
    return <>{fallback}</>
  }

  return <>{children}</>
}
